import { request } from './client'
import type { StaffAuth } from './teaching'
import type { AgeBand } from '../types/prisma-enums'

/**
 * `GET /classes` — the call the teacher tier could not start without.
 *
 * 🔴 THIS ENDPOINT EXISTED FOR THREE UNITS AND NOTHING CALLED IT, WHICH IS WHY
 * `#/teacher` RENDERED *"Couldn't load this."*
 *
 * `AppRouter`'s teacher tier held `const [classId] = useState('')` with a
 * comment saying a class picker did not exist yet — so every teacher screen
 * issued `GET /classes//overview`, which collapses to a path Nest has no route
 * for and answers **404**. The screen reported it faithfully as a load failure.
 * The endpoint to fix it was already shipped, scoped and role-gated.
 *
 * 🔴 THE SCOPING IS THE SERVER'S, AND THAT IS THE WHOLE POINT OF ASKING IT.
 * `ClassesService.list` filters to the classes this principal may see — a
 * teacher gets the ones they teach, a coordinator their assigned ones, an admin
 * their school's. §8.1: *"a guard cannot scope a collection"*, so the service
 * does it. The browser must never filter a class list it was given, because a
 * list it can filter is a list it was wrongly given.
 */

export interface ClassSummary {
  id: string
  name: string
  /**
   * 🔴 "Class 2" / "LKG" / "Nursery" — HOW AN INDIAN SCHOOL ACTUALLY NAMES ONE.
   *
   * Added as a column rather than parsed out of `name`, because parsing breaks
   * the moment a school calls a class "Butterflies" — which is the example the
   * rollover docs themselves use. Null on rows created before the column
   * existed and on schools that do not stream.
   */
  grade: string | null
  /** "A", "B". Null when a grade has a single stream. */
  section: string | null
  /** Drives which games unlock. Always a band, never "age 4". */
  ageBand: AgeBand
  /**
   * 🔴 THE CODE A CHILD TYPES TO SIGN IN, ON THE LIST ITSELF.
   *
   * It used to be reachable only through the play-setup screen's own request,
   * which meant every class list in the product could show a class's name, band,
   * teacher and roll — and not the one value a child needs to get in.
   *
   * ⚠️ `null` IS "NOT ISSUED YET", NOT "NONE". Classes created before this
   * existed get theirs the first time the play-setup screen is opened; new ones
   * are given a code at creation. A screen must say which, because a blank cell
   * reads as a class that is broken rather than one nobody has set up.
   *
   * ⚠️ NOT A CREDENTIAL. It names the class; the child's password or name is
   * what authenticates. Safe on a staff-room screen — and still rotatable.
   */
  code: string | null
  /** `null` when nobody is assigned — the state E4 exists to fix. */
  teacher: { id: string; name: string | null } | null
  childCount: number
  /** Non-null means archived; the roster is history, not a live class. */
  archivedAt: string | null
}

/**
 * ⚠️ ARCHIVED CLASSES ARE EXCLUDED BY DEFAULT, AND THE TEACHER TIER MUST NOT
 * ASK FOR THEM.
 *
 * The server only includes them for the exact string `?includeArchived=true`.
 * D3's archive is how a cohort leaves (*"move the leavers into a Class of 20XX
 * and archive it"*), so an archived class in a teacher's picker is last year's
 * children — a roster they could tap into and start a session against today.
 * The parameter exists for the school tier's history views, and is deliberately
 * not exposed on the call the teacher tier makes.
 */
export const getClasses = (auth: StaffAuth = {}): Promise<ClassSummary[]> =>
  request<ClassSummary[]>('/classes', {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/** One child, carrying the class they sit in. */
export interface StudentRow {
  playerId: string
  name: string
  rollNumber: string | null
  ageBand: AgeBand
  classId: string
  className: string
  grade: string | null
  section: string | null
  parentName: string | null
  /** 🔴 Masked server-side. The full number never reaches the browser. */
  parentPhone: string | null
  lastPlayed: string | null
}

/**
 * `GET /students` — every child the teacher may see, across every class.
 *
 * 🔴 ONE REQUEST, NOT ONE PER SECTION. The obvious build of a class-and-section
 * student list is `classes.map(c => getRoster(c.id))`, which is a round trip per
 * section and grows with the timetable — `architecture.md` §12's *"25 queries
 * for one dashboard"* in the browser instead of the database. The server does it
 * as one scoped query and returns the class on each row.
 *
 * ⚠️ ITS OWN PATH RATHER THAN `/classes/students`: two backend controllers mount
 * `classes` and one declares `:classId`, so a literal segment there would be
 * shadowed by the parameter — silently, resolving to the wrong handler.
 */
export const getAllStudents = (auth: StaffAuth = {}): Promise<StudentRow[]> =>
  request<StudentRow[]>('/students', {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/* ──────────────────────────────────────────────────────── E4 · writes ── */

/**
 * `POST /classes` — the call that makes a class exist.
 *
 * 🔴 THE ENDPOINT HAS BEEN SHIPPED, SCOPED AND TESTED SINCE §8.3 AND NOTHING
 * CALLED IT. Same failure OD-89 records one level up and `getClasses` records in
 * this file: an admin could see their classes and had no way to add one, so a
 * school's structure was created by whoever ran the seed script.
 *
 * ⚠️ NO `schoolId`. It comes from the token — `createClassSchema` has no such
 * field, deliberately, because a field that does not exist cannot be trusted by
 * mistake.
 */
export const createClass = (
  input: {
    name: string
    grade?: string | null
    section?: string | null
    ageBand: AgeBand
    teacherId?: string | null
  },
  auth: StaffAuth = {},
): Promise<ClassSummary> =>
  request<ClassSummary>('/classes', {
    method: 'POST',
    body: input,
    context: 'user',
    token: auth.token,
  })

export interface UpdateClassResult extends ClassSummary {
  /**
   * 🔴 HOW MANY CHILDREN KEPT THEIR OWN BAND WHEN THE CLASS DEFAULT CHANGED.
   * A band change moves nobody — a band is a default, not a diagnosis, and T4
   * lets a teacher override it per child. An admin who set the band to "unlock
   * the right games" will assume otherwise, so the number is reported rather
   * than left to a support ticket.
   */
  childrenKeepingTheirBand: number
}

/**
 * `PATCH /classes/:id` — rename, re-band, or change the class teacher.
 *
 * 🔴 ONLY THE KEYS PRESENT ARE WRITTEN, AND `null` IS NOT ABSENT. `{}` changes
 * nothing and `teacherId: null` means *unassign* — the server reads own-property
 * presence, so a form that spread `undefined` into this body would be asking to
 * leave the field alone rather than to clear it.
 */
export const updateClass = (
  classId: string,
  patch: {
    name?: string
    grade?: string | null
    section?: string | null
    ageBand?: AgeBand
    teacherId?: string | null
  },
  auth: StaffAuth = {},
): Promise<UpdateClassResult> =>
  request<UpdateClassResult>(`/classes/${classId}`, {
    method: 'PATCH',
    body: patch,
    context: 'user',
    token: auth.token,
  })

/**
 * 🔴 IT ARCHIVES. The verb is what a REST client expects and the effect is the
 * only one that is safe: `Player.classId` is `SetNull`, so a hard delete would
 * leave every child in the class with no class — rows intact and invisible to
 * every scoped query in the product, including the admin who ran it.
 */
export const archiveClass = (classId: string, auth: StaffAuth = {}): Promise<ClassSummary> =>
  request<ClassSummary>(`/classes/${classId}`, {
    method: 'DELETE',
    context: 'user',
    token: auth.token,
  })

/* ────────────────────────────────────────────────────────── E4 · subjects ── */

/** A subject the school teaches, named once for the whole school. */
export interface SubjectRow {
  id: string
  name: string
  /** How many classes study it. 🔴 Why a delete can refuse — see `deleteSubject`. */
  classCount: number
}

/** One subject on one class, and who takes it. */
export interface ClassSubjectRow {
  /** 🔴 THE JOIN ROW'S ID, NOT THE SUBJECT'S. See `setSubjectTeacher`. */
  id: string
  subjectId: string
  name: string
  /**
   * `null` means *"the class's own teacher takes it"*, which is the common case.
   * 🔴 THE SCREEN RESOLVES THAT, NOT THE API — copying `Class.teacher` onto every
   * row server-side would be a second place for one fact to live, and the first
   * thing to disagree the day a class changes teacher.
   */
  teacher: { id: string; name: string | null } | null
}

export const getSubjects = (auth: StaffAuth = {}): Promise<SubjectRow[]> =>
  request<SubjectRow[]>('/subjects', {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

export const createSubject = (name: string, auth: StaffAuth = {}): Promise<SubjectRow> =>
  request<SubjectRow>('/subjects', {
    method: 'POST',
    body: { name },
    context: 'user',
    token: auth.token,
  })

export const renameSubject = (
  subjectId: string,
  name: string,
  auth: StaffAuth = {},
): Promise<SubjectRow> =>
  request<SubjectRow>(`/subjects/${subjectId}`, {
    method: 'PATCH',
    body: { name },
    context: 'user',
    token: auth.token,
  })

/**
 * ⚠️ A REAL DELETE, UNLIKE `archiveClass`. Nothing hangs off a subject except
 * the join rows, and the server answers 409 while any exist rather than silently
 * emptying six classes' curricula — which is what a cascade would have done.
 */
export const deleteSubject = (subjectId: string, auth: StaffAuth = {}): Promise<void> =>
  request<void>(`/subjects/${subjectId}`, {
    method: 'DELETE',
    context: 'user',
    token: auth.token,
  })

export const getClassSubjects = (
  classId: string,
  auth: StaffAuth = {},
): Promise<ClassSubjectRow[]> =>
  request<ClassSubjectRow[]>(`/classes/${classId}/subjects`, {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

export const addClassSubject = (
  classId: string,
  input: { subjectId: string; teacherId?: string | null },
  auth: StaffAuth = {},
): Promise<ClassSubjectRow> =>
  request<ClassSubjectRow>(`/classes/${classId}/subjects`, {
    method: 'POST',
    body: input,
    context: 'user',
    token: auth.token,
  })

/**
 * 🔴 ADDRESSED BY THE JOIN ROW, NOT BY THE SUBJECT. *"Change who teaches Maths"*
 * has four answers in a school with four Maths teachers, and exactly one in a
 * given class.
 *
 * ⚠️ `teacherId` IS `string | null` AND NEVER `undefined`. `null` is the real
 * instruction *"hand it back to the class teacher"*; this signature refuses
 * `undefined` so it cannot be sent as "leave it alone" by accident.
 */
export const setSubjectTeacher = (
  classId: string,
  classSubjectId: string,
  teacherId: string | null,
  auth: StaffAuth = {},
): Promise<ClassSubjectRow> =>
  request<ClassSubjectRow>(`/classes/${classId}/subjects/${classSubjectId}`, {
    method: 'PATCH',
    body: { teacherId },
    context: 'user',
    token: auth.token,
  })

/** ⚠️ The school still teaches the subject afterwards — only this class stops. */
export const removeClassSubject = (
  classId: string,
  classSubjectId: string,
  auth: StaffAuth = {},
): Promise<void> =>
  request<void>(`/classes/${classId}/subjects/${classSubjectId}`, {
    method: 'DELETE',
    context: 'user',
    token: auth.token,
  })
